import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useFeatures } from '@jotta/grpc-connect-client/config'
import { downloadPathItem } from '@jotta/grpc-connect-client/content'
import { WopiActionType } from '@jotta/grpc-connect-openapi/wopi'
import { ItemAction, ItemKind } from '@jotta/grpc-js-client/fileService'
import {
  isWopiDocument,
  isWopiPresentation,
  isWopiSpreadsheet,
} from '@jotta/grpc-js-client/wopiService'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgEdit from '@jotta/ui/SvgEdit'
import SvgFileDoc from '@jotta/ui/SvgFileDoc'
import SvgFilePpt from '@jotta/ui/SvgFilePpt'
import SvgFileXls from '@jotta/ui/SvgFileXls'
import SvgFillCaretDown from '@jotta/ui/SvgFillCaretDown'
import SvgFillMoreVertical from '@jotta/ui/SvgFillMoreVertical'

import type { OnContextMenuSelectParams } from '../FileActions/FileActionStore'
import { FileContextMenu } from '../FileContextMenu/FileContextMenu'
import { useWopiLinks } from '../FileView/WopiView/useWopiLinks'
import type { PathItemStore } from '../PathItem/PathItemStore'

export interface FileViewerMenuProps {
  file: PathItemStore
  onEditImage?: (file: PathItemStore) => void
}

const wopiConfig = {
  Excel: {
    icon: SvgFileXls,
    app: 'Microsoft Excel',
    type: 'excel',
  },
  Word: {
    icon: SvgFileDoc,
    app: 'Microsoft Word',
    type: 'word',
  },
  Powerpoint: {
    icon: SvgFilePpt,
    app: 'Microsoft PowerPoint',
    type: 'powerpoint',
  },
} as const satisfies Record<
  string,
  {
    icon: BrandThemeIcon
    app: string
    type: string
  }
>

export const FileViewerMenu = observer<FileViewerMenuProps>(
  function FileViewerMenu({ file, onEditImage }) {
    const { contextMenuActions, onContextMenuSelect, path } = file.listItemData
    const [searchParams, setSearchParams] = useSearchParams()
    const { photoEditorInFilesEnabled } = useFeatures()

    const handleDownloadItem = useCallback(
      () => downloadPathItem(file.model),
      [file],
    )

    const handleOpenDocument = useCallback(
      (type: string) => {
        if (file && type) {
          searchParams.set('action', 'edit')
          setSearchParams(searchParams)
        }
      },
      [file, searchParams, setSearchParams],
    )

    const wopiApp = isWopiDocument(file.name)
      ? wopiConfig.Word
      : isWopiPresentation(file.name)
        ? wopiConfig.Powerpoint
        : isWopiSpreadsheet(file.name)
          ? wopiConfig.Excel
          : undefined

    // we cannot simply check the consent of the authenticated user since
    // the file might be owned by a different user. So we check that we can get the links
    // for this particular file:
    const { data: getWopiLinksResponse } = useWopiLinks(
      file.model,
      WopiActionType.VIEW,
      { enabled: Boolean(wopiApp) },
    )
    const wopiEnabled =
      getWopiLinksResponse?.result.case === 'links' &&
      file.model.actionList.includes(ItemAction.UPDATE)
    const canEditImage =
      photoEditorInFilesEnabled &&
      file.data.kind === ItemKind.IMAGE &&
      Boolean(onEditImage) &&
      file.folder.pathItem.canUpload

    const handleContextMenuSelect = useCallback(
      (params: OnContextMenuSelectParams) => {
        onContextMenuSelect(params)
      },
      [onContextMenuSelect],
    )

    if (!contextMenuActions.length && !canEditImage) {
      return null
    }

    return (
      <div className="grid grid-cols-[auto_auto] items-center text-sx-primary-text lg:grid-cols-fr1-max lg:divide-x lg:divide-sx-primary-text lg:rounded-sm lg:bg-btn-primary">
        {wopiApp && wopiEnabled ? (
          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-sm bg-btn-primary px-2 py-1.5 text-white md:px-3"
            aria-label={t`Open in ${wopiApp.app}`}
            onClick={() => handleOpenDocument(wopiApp.type)}
          >
            <PlainBrandIcon icon={wopiApp.icon} className="size-5 md:size-6" />

            <span className="hidden md:inline lg:hidden">
              <Trans>Open</Trans>
            </span>

            <span className="hidden lg:inline">
              <Trans>Open in {wopiApp.app}</Trans>
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="hidden px-4 lg:block"
            aria-label={t`Download`}
            onClick={handleDownloadItem}
          >
            <Trans>Download</Trans>
          </button>
        )}

        <div className="size-full">
          <FileContextMenu
            path={path}
            context="FOLDER_LIST"
            onSelect={handleContextMenuSelect}
            actions={contextMenuActions}
            customItems={
              canEditImage
                ? [
                    {
                      key: 'edit-image',
                      name: t`Edit`,
                      icon: SvgEdit,
                      onSelect: () => {
                        onEditImage?.(file)
                      },
                    },
                  ]
                : undefined
            }
            type="DROPDOWN"
          >
            <PlainBrandIcon
              icon={SvgFillMoreVertical}
              className="scale-150 fill-white opacity-100 lg:!hidden"
            />
            <PlainBrandIcon
              icon={SvgFillCaretDown}
              className="!hidden fill-white opacity-100 lg:!inline-block"
            />
          </FileContextMenu>
        </div>
      </div>
    )
  },
)
